import { NodeDocContent } from "./nodeDocsData";

export const aiNodesDocs: Record<string, NodeDocContent> = {
  "FILE-OCR": {
    title: "Advanced OCR",
    subtitle: "Text Extraction",
    color: "blue",
    badge: "AI",
    description:
      "Uses optical character recognition (OCR) to extract text from images and PDF documents. This is typically the first step in processing scanned paperwork.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          For best results, ensure documents are high-resolution (<strong>300 DPI</strong>) and free from heavy shadows or text-blocking stamps.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The OCR node analyzes each page of the input document, detects text regions, and extracts the content while attempting to preserve the original layout structure.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Receive document</p>
                <p class="wf-step-desc">Accepts PDF or image files from any upstream trigger or file node.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Analyze pages</p>
                <p class="wf-step-desc">Each page is processed for text detection, layout analysis, and character recognition.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Output text</p>
                <p class="wf-step-desc">Extracted text is returned with layout structure preserved (paragraphs, tables, columns).</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Features</h2>
          <ul class="wf-list">
            <li><strong>Multilingual support</strong> — extracts text from over 100 languages with high accuracy.</li>
            <li><strong>Structure retention</strong> — maintains column layouts, table structures, and paragraph groupings.</li>
            <li><strong>Handwriting detection</strong> — recognizes handwritten text alongside printed characters.</li>
            <li><strong>Multi-page processing</strong> — handles multi-page PDFs, processing each page sequentially.</li>
          </ul>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>extracted_text</code></td><td>string</td><td>The full extracted text from all pages</td></tr>
              <tr><td><code>pages</code></td><td>array</td><td>Per-page text content and metadata</td></tr>
              <tr><td><code>confidence</code></td><td>number</td><td>Overall OCR confidence score (0-1)</td></tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use high-resolution scans (300 DPI) for the best extraction accuracy.</li>
            <li>Chain with Smart Parse or Transform to structure the raw text into fields.</li>
            <li>Check the confidence score to route low-quality extractions for manual review.</li>
          </ul>
        </div>
      </div>
    `,
  },

  SUMMARY: {
    title: "AI Summarizer",
    subtitle: "Content Distillation",
    color: "violet",
    badge: "AI",
    description:
      "Condenses large volumes of text into concise summaries using Large Language Models (LLMs). Perfect for quickly understanding long contracts or meeting notes.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>How it works</h2>
          <p>The AI Summarizer accepts large text inputs and uses an LLM to produce a condensed version. You can choose between different summary styles depending on your needs.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Receive text input</p>
                <p class="wf-step-desc">Accepts text from OCR output, email body, or any text-producing node.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Select summary style</p>
                <p class="wf-step-desc">Choose between bullet points, executive summary, or custom prompt-based style.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Generate summary</p>
                <p class="wf-step-desc">The LLM processes the text and returns a concise summary in the selected format.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Summary styles</h2>
          <table>
            <thead>
              <tr>
                <th>Style</th>
                <th>Description</th>
                <th>Best for</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><strong>Bullet Points</strong></td><td>Concise list of the most important facts and decisions</td><td>Meeting notes, quick reviews</td></tr>
              <tr><td><strong>Executive Summary</strong></td><td>Professional paragraph-style overview of the entire document</td><td>Reports, contracts, legal docs</td></tr>
              <tr><td><strong>Custom Prompt</strong></td><td>You provide specific instructions for what to extract and how to format</td><td>Specialized use cases</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Input Text</code></td>
                <td>The text content to summarize. Connect from OCR, email, or any text source.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Style</code></td>
                <td>Summary format — Bullet Points, Executive Summary, or Custom Prompt.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Custom Prompt</code></td>
                <td>Your specific instructions when using the Custom Prompt style.</td>
                <td>Only for Custom</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use "Executive Summary" for formal documents like contracts and legal agreements.</li>
            <li>Use "Bullet Points" for quick-scan overviews of meeting notes or emails.</li>
            <li>Chain with Word Document node to generate formatted reports from summaries.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "CLOUD-VISION": {
    title: "Google Cloud Vision",
    subtitle: "Image Intelligence",
    color: "indigo",
    badge: "AI",
    description:
      "Powered by Google's Vision AI, this node performs object detection, text extraction, and content moderation on image files.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>Capabilities</h2>
          <p>Google Cloud Vision provides multiple detection modes. The node automatically selects the appropriate mode based on the input file type and your configuration.</p>
          <ul class="wf-list">
            <li><strong>Text extraction (OCR)</strong> — extracts handwritten and typed text from scanned PDF pages and images.</li>
            <li><strong>Object detection</strong> — detects logos, labels, and landmarks in photographic images.</li>
            <li><strong>Content moderation</strong> — flags potentially unsafe or inappropriate image content.</li>
            <li><strong>Document parsing</strong> — understands document structure including headers, paragraphs, and tables.</li>
          </ul>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>File Input</code></td>
                <td>The image or PDF file to analyze. Connect from a trigger or file node.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Detection Mode</code></td>
                <td>Type of analysis — Text, Labels, Logos, or Safe Search.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Cloud Vision vs Advanced OCR</h2>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Advanced OCR</th>
                <th>Cloud Vision</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Text extraction</td><td>Yes</td><td>Yes</td></tr>
              <tr><td>Object/logo detection</td><td>No</td><td>Yes</td></tr>
              <tr><td>Content moderation</td><td>No</td><td>Yes</td></tr>
              <tr><td>Handwriting support</td><td>Yes</td><td>Yes</td></tr>
              <tr><td>Best for</td><td>Document-heavy workflows</td><td>Image analysis + text extraction</td></tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use Cloud Vision when you need object detection or content moderation alongside text extraction.</li>
            <li>For pure text extraction from documents, the Advanced OCR node may be faster.</li>
            <li>Chain with Transform to extract specific labels or text regions from the response.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "GEMINI-UNDERSTANDING": {
    title: "Gemini Smart Extraction",
    subtitle: "Structured Data Extraction",
    color: "purple",
    badge: "AI",
    description:
      "Leverages Google's Gemini Pro models to extract complex data into a strictly defined JSON schema. This is ideal for invoices, receipts, and identity documents.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>How it works</h2>
          <p>Define a JSON schema describing the exact fields you need, and the Gemini model will extract matching data from the input document. The output strictly follows your schema, making it ready for database insertion or API delivery.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Provide document</p>
                <p class="wf-step-desc">Connect a PDF or image file from an upstream node.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Define extraction schema</p>
                <p class="wf-step-desc">Specify the JSON structure with field names and types that you want extracted.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">AI extraction</p>
                <p class="wf-step-desc">Gemini Pro analyzes the document and returns structured data matching your schema.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Schema example</h2>
          <div class="wf-code-block">
            <div class="wf-code-header">extraction_schema.json</div>
            <pre><code>{
  "invoice_id": "string",
  "vendor_name": "string",
  "total_amount": "number",
  "date": "string",
  "line_items": [
    {
      "description": "string",
      "quantity": "number",
      "unit_price": "number"
    }
  ]
}</code></pre>
          </div>
          <p>The AI will strictly follow this schema, making the output directly compatible with databases and APIs.</p>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Document</code></td>
                <td>The PDF or image file to extract data from.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Schema</code></td>
                <td>JSON schema defining the fields, types, and structure to extract.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Instructions</code></td>
                <td>Optional natural language hints to guide the AI (e.g. "dates should be in ISO format").</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Be specific in your schema — include field types and nested structures for best results.</li>
            <li>Use the Instructions field to guide date formats, number handling, or language preferences.</li>
            <li>Chain with MongoDB Connector or Webhook to store or forward the extracted data.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "FILE-COMPARE": {
    title: "Document Comparison",
    subtitle: "Difference Analysis",
    color: "cyan",
    badge: "AI",
    description:
      "Compares two documents and highlights differences in text, formatting, and semantic meaning. Uses AI to summarize the significance of each change.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>How it works</h2>
          <p>Provide two documents — a source (original) and a target (revised) — and the node identifies all differences between them. The AI then summarizes the significance of each change.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Provide source document</p>
                <p class="wf-step-desc">The original document or "before" version.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Provide target document</p>
                <p class="wf-step-desc">The revised document or "after" version to compare against.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Generate diff report</p>
                <p class="wf-step-desc">The node outputs a detailed comparison with highlighted differences and AI-generated change summaries.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Source</code></td>
                <td>The original document (the "before" state). Connect from a trigger or file node.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Target</code></td>
                <td>The revised document (the "after" state) to compare against the source.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>differences</code></td><td>array</td><td>List of detected changes with location and type (added, removed, modified)</td></tr>
              <tr><td><code>summary</code></td><td>string</td><td>AI-generated summary of the overall changes and their significance</td></tr>
              <tr><td><code>similarity_score</code></td><td>number</td><td>Percentage similarity between the two documents (0-100)</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Contract review</strong> — compare contract versions to catch changed terms, clauses, or pricing.</li>
            <li><strong>Compliance checks</strong> — verify that edited documents still conform to the original template.</li>
            <li><strong>Version tracking</strong> — audit how documents have changed over time across workflow runs.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use two separate File Upload nodes to provide source and target documents.</li>
            <li>Chain with IF-ELSE to route documents that exceed a change threshold for manual review.</li>
            <li>The similarity score can be used as a condition in downstream branching logic.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "SMART-PARSE": {
    title: "Document Parsing",
    subtitle: "AI-Powered Data Extraction",
    color: "pink",
    badge: "AI",
    description:
      "Extracts structured data from documents using AI-powered parsing. Combines document store integration, OCR content, and LLM intelligence with a configurable JSON schema for precise field extraction.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          This node adapts its input fields based on connected upstream nodes — it shows <strong>Document Store</strong> fields when connected to a DocIQ trigger, and <strong>Content/Summary</strong> fields when connected to OCR or LLM nodes.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The Document Parsing node intelligently extracts structured data from documents. Define a JSON schema describing the fields you need, provide document content (via store, OCR, or direct text), and the AI returns precisely formatted data matching your schema.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Connect document source</p>
                <p class="wf-step-desc">Link to a Document Store trigger, OCR node, LLM node, or provide content directly. The node auto-detects the source type.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Define extraction schema</p>
                <p class="wf-step-desc">Use the visual Schema Builder to define the JSON structure with field names, types, and nesting for the data you want extracted.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Configure extraction instructions</p>
                <p class="wf-step-desc">Optionally add natural language instructions to guide the AI on formatting, language, or specific extraction rules.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">4</div>
              <div>
                <p class="wf-step-title">AI extraction</p>
                <p class="wf-step-desc">The LLM processes the document content against your schema and returns structured JSON output.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Document Store ID</code></td>
                <td>string</td>
                <td>No</td>
                <td>Reference to the document store. Shown when connected to a Document Store trigger.</td>
              </tr>
              <tr>
                <td><code>File ID(s)</code></td>
                <td>string</td>
                <td>No</td>
                <td>Specific file IDs to process. Supports ForEach iteration for batch processing.</td>
              </tr>
              <tr>
                <td><code>Content</code></td>
                <td>string</td>
                <td>No</td>
                <td>Text content from OCR or LLM nodes. Shown when connected to text-producing nodes.</td>
              </tr>
              <tr>
                <td><code>Summary</code></td>
                <td>string</td>
                <td>No</td>
                <td>Summary text from upstream LLM or OCR nodes.</td>
              </tr>
              <tr>
                <td><code>Multiple Files</code></td>
                <td>string</td>
                <td>No</td>
                <td>Array of files or collection reference for batch processing.</td>
              </tr>
              <tr>
                <td><code>Additional Content</code></td>
                <td>string</td>
                <td>No</td>
                <td>Extra context or data to include in the extraction process.</td>
              </tr>
              <tr>
                <td><code>LLM Model Name</code></td>
                <td>string</td>
                <td>No</td>
                <td>The AI model to use (e.g. <code>gemini-1.5-flash</code>, <code>gemini-2.5-flash-lite</code>).</td>
              </tr>
              <tr>
                <td><code>Prompt</code></td>
                <td>string</td>
                <td>No</td>
                <td>Custom instructions guiding the extraction (e.g. "Extract amounts in USD", "Parse dates as ISO format").</td>
              </tr>
              <tr>
                <td><code>Schema</code></td>
                <td>object</td>
                <td>No</td>
                <td>JSON schema defining the structure and fields to extract. Built via the visual Schema Builder.</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>extracted_data</code></td><td>object</td><td>Structured data matching the defined schema</td></tr>
              <tr><td><code>raw_response</code></td><td>string</td><td>The raw AI model response before schema formatting</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Context-aware inputs</h2>
          <p>The node dynamically shows different input fields depending on which nodes are connected upstream:</p>
          <table>
            <thead>
              <tr>
                <th>Connected to</th>
                <th>Fields shown</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><strong>Document Store</strong> trigger</td><td>Document Store ID, File ID(s)</td></tr>
              <tr><td><strong>OCR / Cloud Vision</strong> node</td><td>Content, Summary</td></tr>
              <tr><td><strong>LLM / Summarize</strong> node</td><td>Content, Summary</td></tr>
              <tr><td><strong>No connection</strong></td><td>Content, Summary (manual input)</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Invoice data extraction</p>
              <p class="wf-use-case-flow">Document Upload &rarr; OCR &rarr; Document Parsing &rarr; MongoDB</p>
              <p>Extract invoice numbers, line items, totals, and vendor details into structured JSON for database storage.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Batch document processing</p>
              <p class="wf-use-case-flow">DocIQ Trigger &rarr; For-Each &rarr; Document Parsing &rarr; Excel Export</p>
              <p>Process multiple documents from a store, extracting key fields from each into a consolidated spreadsheet.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Identity document verification</p>
              <p class="wf-use-case-flow">Document Upload &rarr; Document Parsing &rarr; IF-ELSE &rarr; Webhook</p>
              <p>Extract name, ID number, and expiry date from identity documents, then route based on validation rules.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use the visual Schema Builder to define complex nested structures without writing JSON manually.</li>
            <li>Add specific instructions in the Prompt field to improve extraction accuracy for domain-specific documents.</li>
            <li>Combine with For-Each to batch-process multiple files from a document store.</li>
            <li>Drag variables from upstream nodes in the 3-column configuration modal for quick input mapping.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "EMAIL-OCR": {
    title: "Email Intelligence",
    subtitle: "Semantic Email Parsing",
    color: "amber",
    badge: "AI",
    description:
      "Analyzes email content (subject and body) to extract structured information like invoice numbers, meeting dates, or customer sentiment.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Connect this node directly to the <strong>Outlook</strong> or <strong>Gmail</strong> trigger to automatically populate the email context.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The Email Intelligence node uses AI to parse email subject and body text, extracting structured information based on the content type. It understands context, not just keywords.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Receive email data</p>
                <p class="wf-step-desc">Accepts email content from Gmail or Outlook triggers (subject, body, sender, etc.).</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">AI analysis</p>
                <p class="wf-step-desc">The AI parses the email content and extracts relevant structured information.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Output structured data</p>
                <p class="wf-step-desc">Returns extracted fields (invoice numbers, dates, amounts, sentiment) as structured JSON.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Email Content</code></td>
                <td>The email subject and body text. Typically auto-populated from Gmail/Outlook trigger.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Invoice extraction</p>
              <p class="wf-use-case-flow">Gmail Trigger &rarr; Email Intelligence &rarr; Transform &rarr; Database</p>
              <p>Automatically extract invoice numbers, amounts, and due dates from billing emails.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Support ticket categorization</p>
              <p class="wf-use-case-flow">Outlook Trigger &rarr; Email Intelligence &rarr; Switch &rarr; Route by Category</p>
              <p>Classify incoming support emails by sentiment and topic, then route to the appropriate team.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Works best when connected directly to a Gmail or Outlook trigger node.</li>
            <li>Chain with IF-ELSE to route emails by extracted category or sentiment.</li>
            <li>Use the Switch node to create multi-path routing based on email type.</li>
          </ul>
        </div>
      </div>
    `,
  },
};
